import { logger } from '@axilon-apps/libs-ui-core'
import type { IPlatformIdentityServices } from './types'

let _instance: Readonly<IPlatformIdentityServices> | null = null

/**
 * Register the platform services implementation. Identity-aware host apps can pass
 * a wider object that extends `IPlatformIdentityServices` (e.g. axilon-devops's
 * full `IPlatformServices`); identity-side consumers will see the narrower view
 * while host-side consumers cast back to their wider type via the generic on
 * `getPlatformServices<T>()` / `usePlatform<T>()`.
 *
 * The registered object is shallow-frozen, so callers cannot mutate service
 * slots after registration — get a fresh object and re-register instead.
 *
 * WARNING: this is a process-wide singleton. Mounting two `<PlatformProvider>`s
 * (or otherwise calling `setPlatformServices` twice) in the same process will
 * clobber the previous registration, and every non-React consumer
 * (`getPlatformServices()` in Redux listeners, middleware, etc.) will silently
 * switch to the most recently registered instance. Host apps should register
 * exactly once at startup.
 */
export function setPlatformServices<T extends IPlatformIdentityServices>(services: T): void {
  if (_instance && _instance !== services) {
    logger.warn(
      '[identity] setPlatformServices called more than once with a different instance; ' +
        'the previous registration has been clobbered. This is a process-wide singleton — ' +
        'register exactly once at startup.'
    )
  }
  _instance = Object.freeze(services)
}

/**
 * Retrieve the registered platform services. Works in both React and non-React
 * contexts (Redux listeners, middleware). The returned object is frozen.
 */
export function getPlatformServices<
  T extends IPlatformIdentityServices = IPlatformIdentityServices
>(): T {
  if (!_instance) {
    throw new Error(
      'PlatformServices not initialised. Call setPlatformServices() before accessing platform APIs.'
    )
  }
  return _instance as T
}
